// components/NotificationCenter.tsx
import React, { useEffect, useState } from 'react';
import { Bell, X, CheckCircle2, AlertTriangle, Info, Clock } from 'lucide-react';
import Card from './Card';
import Button from './Button';
import apiService from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import toast from 'react-hot-toast';

const NotificationCenter: React.FC = () => {
  const { user } = useAuth();
  const {
    notifications,
    replaceNotifications,
    markAllAsRead,
    markOneAsRead,
    removeNotification,
  } = useSocket();

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await apiService.notifications.getMy();
      // Alimente l’état global du Provider (pas local)
      const list = res?.data?.notifications ?? res?.data?.data?.notifications ?? [];
      replaceNotifications(list);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      toast.error('Erreur lors du chargement des notifications.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && user?.id) fetchNotifications();
  }, [isOpen, user?.id]);

  const unreadCount = (notifications || []).filter(n => !n.isRead).length;

  const handleMarkAsRead = async (id: string) => {
    try {
      await apiService.notifications.markAsRead(id);
      markOneAsRead(id);
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await apiService.notifications.markAllAsRead();
      markAllAsRead();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await apiService.notifications.delete(id);
      removeNotification(id);
    } catch (err) {
      console.error('Error removing notification:', err);
    }
  };

  const getIcon = (type?: string) => {
    switch (type) {
      case 'mission_assigned':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'status_update':
        return <Info className="w-5 h-5 text-blue-500" />;
      case 'new_message':
        return <Bell className="w-5 h-5 text-yellow-500" />;
      case 'system':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default:
        return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="relative"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 z-50">
          <Card className="shadow-xl border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                <div className="flex items-center space-x-2">
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={handleMarkAllAsRead}>
                      Tout marquer lu
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center text-gray-500">Chargement…</div>
              ) : (notifications || []).length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p>Aucune notification</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {notifications.map((n) => {
                    const id = String(n._id ?? n.id);
                    return (
                      <div
                        key={id}
                        className={`p-4 hover:bg-gray-50 cursor-pointer ${
                          !n.isRead ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        }`}
                        onClick={() => handleMarkAsRead(id)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-0.5">{getIcon(n.type)}</div>
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-sm text-gray-600 mt-1 ${
                                !n.isRead ? 'font-semibold' : ''
                              }`}
                            >
                              {n.message}
                            </p>
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center text-xs text-gray-500">
                                <Clock className="w-3 h-3 mr-1" />
                                {n.createdAt
                                  ? new Date(n.createdAt).toLocaleString('fr-FR')
                                  : ''}
                              </div>
                              {n.relatedEntity && (
                                <span className="text-xs text-blue-600">
                                  {n.relatedEntity.type} {n.relatedEntity.id}
                                </span>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemove(id);
                            }}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
